import * as React from "react";
import {ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon} from "lucide-react";
import {cn} from "../../utils/utils.ts";
import {Button, buttonVariants} from "../basic/button.tsx";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "../basic/select.tsx";
import {useTranslationWithKeys} from "../../hooks/useTranslationWithKeys";

// Base pagination components (keeping existing for backward compatibility)
function Pagination({ className, ...props }: React.ComponentProps<"nav">) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn("mx-auto flex w-full justify-center", className)}
      {...props}
    />
  );
}

function PaginationContent({
  className,
  ...props
}: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn("flex flex-row items-center gap-1", className)}
      {...props}
    />
  );
}

function PaginationItem({ ...props }: React.ComponentProps<"li">) {
  return <li data-slot="pagination-item" {...props} />;
}

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<React.ComponentProps<typeof Button>, "size"> &
  React.ComponentProps<"a">;

function PaginationLink({
  className,
  isActive,
  size = "icon",
  ...props
}: PaginationLinkProps) {
  return (
    <a
      aria-current={isActive ? "page" : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={cn(
        buttonVariants({
          variant: isActive ? "outline" : "ghost",
          size,
        }),
        className,
      )}
      {...props}
    />
  );
}

function PaginationPrevious({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      className={cn("gap-1 px-2.5 sm:pl-2.5", className)}
      {...props}
    >
      <ChevronLeftIcon />
      <span className="hidden sm:block">Previous</span>
    </PaginationLink>
  );
}

function PaginationNext({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      className={cn("gap-1 px-2.5 sm:pr-2.5", className)}
      {...props}
    >
      <span className="hidden sm:block">Next</span>
      <ChevronRightIcon />
    </PaginationLink>
  );
}

function PaginationEllipsis({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn("flex size-9 items-center justify-center", className)}
      {...props}
    >
      <MoreHorizontalIcon className="size-4" />
      <span className="sr-only">More pages</span>
    </span>
  );
}

// Reusable pagination hook
export function usePagination<T>(
    data: T[],
    initialPageSize: number = 10,
    pageSizeOptions: number[] = [10, 25, 50, 100]
) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(initialPageSize);

  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize]);

  const totalPages = React.useMemo(() => {
    return Math.ceil(data.length / pageSize);
  }, [data.length, pageSize]);

  const paginationInfo = React.useMemo(() => {
    const start = data.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, data.length);
    return {start, end, total: data.length};
  }, [currentPage, pageSize, data.length]);

  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = React.useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const goToPreviousPage = React.useCallback(() => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = React.useCallback(() => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  const resetPagination = React.useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    currentPage,
    pageSize,
    totalPages,
    paginatedData,
    paginationInfo,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
    goToPreviousPage,
    goToNextPage,
    resetPagination,
  };
}

// Comprehensive pagination component
export interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  pageSizeOptions: number[];
  paginationInfo: {
    start: number;
    end: number;
    total: number;
  };
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  rowsPerPageLabel?: string;
  previousLabel?: string;
  nextLabel?: string;
  showPageSizeSelector?: boolean;
  showPaginationInfo?: boolean;
  maxVisiblePages?: number;
  className?: string;
}

export function PaginationControls({
                                     currentPage,
                                     totalPages,
                                     pageSize,
                                     pageSizeOptions,
                                     paginationInfo,
                                     onPageChange,
                                     onPageSizeChange,
                                     rowsPerPageLabel = "Rows per page:",
                                     previousLabel = "Previous",
                                     nextLabel = "Next",
                                     showPageSizeSelector = true,
                                     showPaginationInfo = true,
                                     maxVisiblePages = 5,
                                     className,
                                   }: PaginationControlsProps) {
  const {t} = useTranslationWithKeys();
  // Generate page numbers to show
  const getVisiblePages = React.useCallback(() => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({length: totalPages}, (_, i) => i + 1);
    }

    const pages: number[] = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);

    // Always show first page
    pages.push(1);

    // Calculate middle range
    let startPage = Math.max(2, currentPage - halfVisible + 1);
    const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

    // Adjust if we're too close to the end
    if (endPage - startPage < maxVisiblePages - 3) {
      startPage = Math.max(2, endPage - (maxVisiblePages - 3));
    }

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }, [currentPage, totalPages, maxVisiblePages]);

  const visiblePages = getVisiblePages();

  const shouldShowEllipsis = (index: number) => {
    if (index === 0 || index === visiblePages.length - 1) return false;
    return visiblePages[index] - visiblePages[index - 1] > 1;
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
      <div className={cn("flex items-center justify-between", className)}>
        <div className="flex items-center gap-2">
          {showPageSizeSelector && (
              <>
                <span className="text-muted-foreground">{rowsPerPageLabel}</span>
                <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => onPageSizeChange(Number(value))}
                >
                  <SelectTrigger className="w-[70px]">
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent>
                    {pageSizeOptions.map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}
                        </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
          )}

          {showPaginationInfo && (
              <span className="text-muted-foreground">
            {t('common.paginationInfo', {
              start: paginationInfo.start,
              end: paginationInfo.end,
              total: paginationInfo.total
            })}
          </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
          >
            {previousLabel}
          </Button>

          {visiblePages.map((page, index) => (
              <React.Fragment key={page}>
                {shouldShowEllipsis(index) && <PaginationEllipsis/>}
                <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(page)}
                >
                  {page}
                </Button>
              </React.Fragment>
          ))}

          <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
          >
            {nextLabel}
          </Button>
        </div>
      </div>
  );
}

// Combined pagination component that includes both hook and controls
export interface PaginationComponentProps<T> {
  data: T[];
  initialPageSize?: number;
  pageSizeOptions?: number[];
  rowsPerPageLabel?: string;
  previousLabel?: string;
  nextLabel?: string;
  showPageSizeSelector?: boolean;
  showPaginationInfo?: boolean;
  maxVisiblePages?: number;
  className?: string;
  children: (paginatedData: T[], paginationInfo: { start: number; end: number; total: number }) => React.ReactNode;
}

export function PaginationComponent<T>({
                                         data,
                                         initialPageSize = 10,
                                         pageSizeOptions = [10, 25, 50, 100],
                                         rowsPerPageLabel,
                                         previousLabel,
                                         nextLabel,
                                         showPageSizeSelector,
                                         showPaginationInfo,
                                         maxVisiblePages,
                                         className,
                                         children,
                                       }: PaginationComponentProps<T>) {
  const pagination = usePagination(data, initialPageSize, pageSizeOptions);

  return (
      <>
        {children(pagination.paginatedData, pagination.paginationInfo)}
        <PaginationControls
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            pageSize={pagination.pageSize}
            pageSizeOptions={pagination.pageSizeOptions}
            paginationInfo={pagination.paginationInfo}
            onPageChange={pagination.handlePageChange}
            onPageSizeChange={pagination.handlePageSizeChange}
            rowsPerPageLabel={rowsPerPageLabel}
            previousLabel={previousLabel}
            nextLabel={nextLabel}
            showPageSizeSelector={showPageSizeSelector}
            showPaginationInfo={showPaginationInfo}
            maxVisiblePages={maxVisiblePages}
            className={className}
        />
      </>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};